import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE from '../apiBase';
import { toCommonFileResponse } from '../adapters/prAdapters';

export default function useFileDiffs({ provider, identifiers, getProjectKey }) {
  const { organization, project, repositoryID, prNumber } = identifiers;
  const [fileCache, setFileCache] = useState({}); 
  const [loadingFileMap, setLoadingFileMap] = useState({}); 
  const bgControllersRef = useRef(new Set());

  const makeBasePath = () => {
    if (provider === 'github') return `${API_BASE}/api/github/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}`;
    if (provider === 'bitbucket') return `${API_BASE}/api/bitbucket/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}`;
    if (provider === 'azure') return `${API_BASE}/api/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${encodeURIComponent(repositoryID)}/${prNumber}`;
    if (provider === 'gitlab') {
      const projectID = organization; // for GitLab page we pass projectID in organization slot
      return `${API_BASE}/api/gitlab/projects/${encodeURIComponent(projectID)}/merge-requests/${prNumber}`;
    }
    return `${API_BASE}/api/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${encodeURIComponent(repositoryID)}/${prNumber}`;
  };

  const ensureFileLoaded = useCallback(async (path, { trackAbort = false } = {}) => {
    if (provider === 'azure') return; 
    const key = (path || '').toString().replace(/\\/g, '/').replace(/\s+/g, ' ').trim();
    if (!key || loadingFileMap[key] || fileCache[key]) return;
    try {
      setLoadingFileMap(prev => ({ ...prev, [key]: true }));
      const projectKey = getProjectKey();
      if (!projectKey) return;
      const base = makeBasePath();
      const controller = trackAbort ? new AbortController() : null;
      if (controller) bgControllersRef.current.add(controller);
      try {
        const { data } = await axios.get(`${base}/file`, { params: { path: key }, headers: { 'X-API-KEY': projectKey }, signal: controller ? controller.signal : undefined });
        const normalized = toCommonFileResponse(provider, data || {});
        setFileCache(prev => ({ ...prev, [key]: normalized }));
      } catch (error) {
        if (error && (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || (typeof axios.isCancel === 'function' && axios.isCancel(error)))) {
          // ignore
        }
      } finally {
        if (controller) { try { bgControllersRef.current.delete(controller); } catch {} }
      }
    } finally {
      setLoadingFileMap(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  }, [provider, identifiers, getProjectKey, loadingFileMap, fileCache]);

  const abortAll = useCallback(() => {
    try {
      bgControllersRef.current.forEach((c) => { try { c.abort(); } catch {} });
      bgControllersRef.current.clear();
    } catch {}
  }, []);

  return { fileCache, setFileCache, loadingFileMap, setLoadingFileMap, ensureFileLoaded, abortAll };
}



